<template>
  <div
    class="col full-height q-pb-sm attendance-transfer"
    style="max-width: 240px"
  >
    <div class="row height-30px q-mb-xs q-mt-sm">
      <div class="col q-px-sm">
        <div class="row full-height content-center">
          <SearchIcon class="search-icon q-ma-sm absolute" />
          <input
            data-testid="transferencia-busca"
            type="text"
            class="fit input-search font-search"
            v-model="filter"
            placeholder="Buscar agente ou fila..."
          >
        </div>
      </div>
    </div>

    <div
      v-if="isPreference"
      class="row height-30px q-ml-xs"
    >
      <q-checkbox
        data-testid="transferencia-preferencia"
        class="text-caption"
        size="xs"
        label="Transferir preferência"
        v-model="changeOwner"
      />
    </div>

    <div
      v-if="loading"
      class="row full-width height-30px q-mb-xs q-mt-sm"
    >
      <div class="col full-height row items-center justify-center">
        <q-spinner-dots
          size="20px"
          color="primary"
        />
      </div>
    </div>

    <div
      v-for="section in sections"
      :key="section.id"
      class="section-container q-mt-sm"
    >
      <div
        class="section-header q-px-sm q-py-xs cursor-pointer"
        :data-testid="`transferencia-secao-${section.id}`"
        @click="section.expanded = !section.expanded"
      >
        <span class="text-caption text-weight-medium">
          <q-icon
            :name="section.expanded ? 'mdi-chevron-down' : 'mdi-chevron-right'"
            size="16px"
          />
          {{ section.label }} ({{ section.items.length }})
        </span>
      </div>

      <div
        v-show="section.expanded"
        class="rounded-borders full-width q-px-xs"
      >
        <div
          v-for="item in section.items"
          :key="item.key"
          :class="['row items-center item-slice', item.canTransfer ? 'cursor-pointer' : 'cursor-block']"
          :data-testid="`transferencia-${item.key}`"
          @click="onClickItem(item)"
        >
          <div class="col q-px-sm tw-min-w-0">
            <q-item-label
              class="ellipsis"
              :disabled="!item.canTransfer"
            >
              <span :class="item.canTransfer ? 'text-green-8' : 'text-red-8'">
                {{ item.description }}
                <span
                  style="font-size:80%"
                  :class="!item.canTransfer ? 'text-red-8' : ''"
                >
                  ({{ item.number }})
                </span>
              </span>
            </q-item-label>
          </div>

          <q-tooltip
            :content-class="item.canTransfer ? 'bg-green-8' : 'bg-red-8'"
            content-style="font-size: 12px"
          >
            <span>{{ item.canTransfer ? 'Transferir para' : 'Não é possível transferir para' }}</span>
            <span class="text-bold">
              {{ item.description }}
              <span style="font-size: 80%">({{ item.number }})</span>
            </span>
          </q-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import SearchIcon from '@/assets/icons/search.svg'
import getEntryNumbers from '@/libs/getEntryNumbers'
import api from '@/libs/api'

const normalize = (str) =>
  String(str || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()

const itemMatchesFilter = (item, filter) => {
  const f = normalize(filter)
  if (!f) return true
  return normalize(item.number).includes(f) || normalize(item.description).includes(f)
}

const defaultCanTransfer = (item) => {
  const agent = item.status || ''
  const ext = item.extension?.status || ''

  if (['OFFLINE', 'PAUSED'].includes(agent)) return false
  if (['Unreachable', 'Unknown'].includes(ext)) return false
  if ((!agent && !ext) && (item.agentId || item.extensionId)) return false

  return true
}

export default {
  components: { SearchIcon },

  props: {
    chatId: {},
    agentId: {},
    queueId: {},
    direction: {},
    destination: {}
  },

  data () {
    return {
      filter: '',
      agents: [],
      queues: [],
      changeOwner: false,
      loading: false,
      isPreference: false,
      elMounted: false,

      sections: [
        { id: 'agents', label: 'Agentes', expanded: false, items: [] },
        { id: 'queues', label: 'Filas', expanded: false, items: [] }
      ]
    }
  },

  computed: {
    ...mapGetters({
      isAdmin: 'settingsModule/isAdmin',
      sessionMembership: 'agentModule/sessionMembership'
    })
  },

  async mounted () {
    this.elMounted = true
    this.loading = true

    if (this.destination) this.filter = this.destination

    await this.$store.dispatch('agentModule/getQueuesProperties', this.queueId)
    const props = this.$store.state.agentModule.queuesProperties[this.queueId] || {}
    this.isPreference = props.attendanceExclusive === 'preference' && this.direction === 'IN'

    try {
      const entry = await getEntryNumbers(true)
      if (!this.elMounted) return

      this.agents = entry.filter(a =>
        a.destinationType === 'AGENTCODE' &&
        a.status !== 'OFFLINE' &&
        Number(a.agentId) !== Number(this.agentId)
      )

      const resp = await api.getQueues()
      const rawQueues = resp.data || resp || []

      this.queues = rawQueues.map(q => ({
        queueId: q.queueId || q.id,
        description: q.name || q.description || q.queueId,
        number: q.queueId || q.id,
        hasAvailableAgents: true
      }))

      this.refreshSectionItems()

      const hasNoOptions = !this.sections[0].items.length && !this.sections[1].items.length
      if (hasNoOptions) this.$emit('close-transfer-options')
    } catch (err) {
      console.error('Error loading data:', err)
    } finally {
      this.loading = false
    }
  },

  watch: {
    filter () {
      this.refreshSectionItems()
    }
  },

  beforeDestroy () {
    this.elMounted = false
  },

  methods: {
    ...mapActions({ sendTransfer: 'socketModule/sendTransfer' }),

    refreshSectionItems () {
      const filter = this.filter

      const agentItems = this.agents
        .filter(a => itemMatchesFilter(a, filter))
        .map(a => ({
          key: `agent-${a.id}`,
          type: 'AGENT',
          number: a.number,
          description: a.description,
          raw: a,
          canTransfer: defaultCanTransfer(a)
        }))
        .sort((a, b) => Number(b.canTransfer) - Number(a.canTransfer))

      const queueItems = this.queues
        .filter(q => itemMatchesFilter(q, filter))
        .map(q => ({
          key: `queue-${q.queueId}`,
          type: 'QUEUE',
          number: q.number,
          description: q.description,
          raw: q,
          canTransfer: q.hasAvailableAgents !== false
        }))
        .sort((a, b) => Number(b.canTransfer) - Number(a.canTransfer))

      this.sections[0].items = agentItems
      this.sections[1].items = queueItems
    },

    onClickItem (item) {
      if (!item.canTransfer) {
        this.$emit('hide-menu')
        return
      }

      const values = {
        chatId: this.chatId,
        changeOwner: this.changeOwner
      }

      if (item.type === 'AGENT') {
        values.destination = item.raw.agentId
      } else {
        values.destination = item.raw.queueId
        values.destinationType = 'QUEUE'
      }

      this.sendTransfer(values)
      this.$emit('hide-menu')
    }
  }
}
</script>

<style lang="sass" scoped>
.cursor-block
  cursor: not-allowed !important

.cursor-pointer
  cursor: pointer !important

.item-slice
  border-bottom: 1px solid #ddd
  padding: 5px 0
  margin-bottom: 5px

.section-header
  background-color: #f5f5f5
  border-radius: 3px
  user-select: none
  &:hover
    background-color: #eeeeee

.attendance-transfer
  .input-search
    border: 1px solid #CDDCE5
    border-radius: 3px
    padding-left: 28px
    color: #01253C
    &:focus
      border: 1px solid #1769B6
      outline: none
    &:hover
      border: 1px solid #cacaca
    &::-webkit-input-placeholder
      color: #01253C
      opacity: 0.5
      font-size: 12px

  .search-icon
    height: 16px
    width: 16px

.height-30px
  height: 30px
  min-height: 30px
</style>
